import { useState, useCallback, useEffect, useRef } from "react";

interface UseKeyboardNavigationOptions<T> {
  items: T[];
  onSelect: (item: T) => void;
  enabled?: boolean;
}

/**
 * Hook for keyboard navigation (arrow keys + enter) through a list of items.
 * Attach `onKeyDown` to the search input and add `data-nav-index={index}` to each item element.
 */
export function useKeyboardNavigation<T>({
  items,
  onSelect,
  enabled = true,
}: UseKeyboardNavigationOptions<T>) {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Use refs to avoid stale closures in the keydown handler
  const highlightedIndexRef = useRef(highlightedIndex);
  highlightedIndexRef.current = highlightedIndex;

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Reset highlight when items change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [items.length]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    const currentItems = itemsRef.current;
    if (!enabledRef.current || currentItems.length === 0) return;

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const idx = highlightedIndexRef.current;
        const nextIndex = idx < currentItems.length - 1 ? idx + 1 : 0;
        setHighlightedIndex(nextIndex);
        document
          .querySelector(`[data-nav-index="${nextIndex}"]`)
          ?.scrollIntoView({ block: "nearest" });
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const idx = highlightedIndexRef.current;
        const prevIndex = idx > 0 ? idx - 1 : currentItems.length - 1;
        setHighlightedIndex(prevIndex);
        document
          .querySelector(`[data-nav-index="${prevIndex}"]`)
          ?.scrollIntoView({ block: "nearest" });
        break;
      }
      case "Enter": {
        const idx = highlightedIndexRef.current;
        if (idx >= 0 && idx < currentItems.length) {
          e.preventDefault();
          onSelectRef.current(currentItems[idx]);
        }
        break;
      }
      case "Escape": {
        setHighlightedIndex(-1);
        break;
      }
    }
  }, []);

  return { highlightedIndex, setHighlightedIndex, onKeyDown };
}
